import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { CloseIcon } from '@wf-sl/common-icons'

import { getExtraneousProps } from '../../util/react'

import styles from './tag.module.css'

const propTypes = {
    categoryClassName: PropTypes.string,
    children: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
    indicatorColor: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    style: PropTypes.object // eslint-disable-line react/forbid-prop-types
}

/**
 * Renders a tag element, can be closable and have a category or customized indicator color.
 * The categoryClassName parameter has the highest rank, category is next, indicatorColor is lowest rank color.
 * @example
 * <Tag>I am a Tag!</Tag>
 *
 * @param {object|array}    props.children              - child elements of a tag, usually a text
 * @param {string}          props.className             - css class to add to the styling of the element
 * @param {object}          props.style                 - css class to add to the styling of the element
 * @param {string}          props.categoryClassName     - css class to add to the styling of the indicator element
 * @param {string}          props.contentClassName      - the name of a predefined color for the indicator element
 * @param {string}          props.indicatorColor        - DEPRECATED: Use categoryClassName instead
 * @param {function}        props.onClose               - callback to call when the close icon is clicked
 * @param {string}          props.icon                  - path to the image file that should be appended as icon
 */
const Tag = props => {
    const { categoryClassName, children, className, contentClassName, icon, indicatorColor, onClick, onClose, style } =
        props

    const composedClassName = cx(styles['tag'], 't3sel-tag', className)
    const backgroundColorStyle = indicatorColor && !categoryClassName ? { backgroundColor: indicatorColor } : null

    const indicatorElement =
        categoryClassName || indicatorColor ? (
            <div className={cx(styles['indicator'], categoryClassName)} style={backgroundColorStyle} />
        ) : null
    let iconElement = null
    if (typeof icon === 'string') {
        iconElement = <img src={icon} className="icon" />
    } else if (icon) {
        iconElement = React.createElement(icon, {
            className: cx(styles['icon'], { [styles['icon-with-indicator']]: !!indicatorElement }),
            keepPathColor: true,
            width: indicatorElement ? 25 : 18
        })
    }
    const closeIcon = onClose ? (
        <div className={styles['close']}>
            <CloseIcon onClick={onClose} className={styles['close-icon']} />
        </div>
    ) : null

    // other props that we can pass, e.g. mouse events for the Tooltip component
    const otherProps = getExtraneousProps(propTypes, props)

    return (
        <div className={composedClassName} style={style} onClick={onClick} {...otherProps}>
            {indicatorElement}
            <span className={cx(styles['content'], 't3sel-tag-content', contentClassName)}>
                {iconElement} {children}
            </span>
            {closeIcon}
        </div>
    )
}

Tag.propTypes = propTypes

export default Tag
